import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

interface AuthRedirectProps {
  children: React.ReactNode;
  redirectTo?: string;
  showMessage?: string;
  showToast?: (message: string, type?: 'success' | 'error') => void;
}

/**
 * Composant qui redirige les utilisateurs déjà connectés
 * Utilisé pour les pages de connexion/inscription
 */
export const AuthRedirect: React.FC<AuthRedirectProps> = ({ 
  children, 
  redirectTo = '/profile',
  showMessage = 'Vous êtes déjà connecté-e !',
  showToast 
}) => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && user) {
      if (showToast) {
        showToast(showMessage, 'success');
      }
      navigate(redirectTo);
    }
  }, [user, loading, navigate, redirectTo, showMessage, showToast]);

  // Afficher un loader pendant la vérification
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-[#002c46] mx-auto mb-4"></div>
          <p className="text-gray-600">Vérification de votre statut...</p>
        </div>
      </div>
    );
  }

  // Si utilisateur connecté, ne pas afficher le contenu (redirection en cours)
  if (user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Redirection en cours...</p>
        </div>
      </div>
    );
  }

  // Utilisateur non connecté, afficher le contenu normal
  return <>{children}</>;
};
